import { useMemo, useState } from "react";
import {
  IconFile,
  IconFileTypePdf,
  IconMusic,
  IconPhoto,
  IconVideo,
} from "@tabler/icons-react";
import CmsLayout from "@layouts/CmsLayout";
import ActionStepMap from "@components/mappings/ActionStepMap";
import IndexTableMap from "@components/mappings/IndexTableMap";
import AppModal from "@components/modals/AppModal";
import ConfirmDeleteModal from "@components/modals/ConfirmDeleteModal";
import env from "@configs/env";
import useMultimedia from "@hooks/multimedia/useMultimedia";
import { filterRows, paginateRows } from "@utils/indexTable";
import MediaAdd from "@pages/media/add";
import MediaUpdate from "@pages/media/update";

function resolveMediaUrl(rawUrl) {
  if (!rawUrl) return "";
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://"))
    return rawUrl;
  const base = (process.env.REACT_APP_API_BASE_URL || "").replace(
    /\/api\/?$/,
    "",
  );
  return `${base}${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`;
}

function MediaPreviewCell({ item, onPreview }) {
  const [hasError, setHasError] = useState(false);
  const rawUrl = item.mmd_url || item.url || "";
  const url = resolveMediaUrl(rawUrl);
  const mediaType = (item.mmd_media_type || item.mediaType || "").toLowerCase();
  const mimeType = (item.mmd_mime_type || item.mimeType || "").toLowerCase();
  const isImage = mediaType === "image" || mimeType.startsWith("image/");
  const isVideo = mediaType === "video" || mimeType.startsWith("video/");
  const isPreviewable = isImage || isVideo;

  if (!url || hasError) return <IconFile size={18} />;

  function handleClick() {
    if (isPreviewable) {
      onPreview({ url, mediaType, mimeType });
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }

  if (isImage) {
    return (
      <button
        type="button"
        className="media-preview-button"
        onClick={handleClick}
      >
        <img
          src={url}
          alt="preview"
          className="media-thumb"
          loading="lazy"
          onError={() => setHasError(true)}
        />
      </button>
    );
  }

  if (isVideo) {
    return (
      <button
        type="button"
        className="media-preview-button"
        onClick={handleClick}
      >
        <video
          className="media-thumb"
          muted
          preload="metadata"
          onError={() => setHasError(true)}
        >
          <source src={url} type={mimeType || undefined} />
        </video>
      </button>
    );
  }

  const isAudio = mediaType === "audio" || mimeType.startsWith("audio/");
  if (isAudio) {
    return (
      <button
        type="button"
        className="media-preview-button media-file-button"
        onClick={handleClick}
      >
        <IconMusic size={16} />
        <span>Open</span>
      </button>
    );
  }

  const isPdf = mimeType.includes("pdf") || url.toLowerCase().endsWith(".pdf");
  if (isPdf) {
    return (
      <button
        type="button"
        className="media-preview-button media-file-button"
        onClick={handleClick}
      >
        <IconFileTypePdf size={18} />
        <span>Open</span>
      </button>
    );
  }

  if (mediaType === "image") return <IconPhoto size={18} />;
  if (mediaType === "video") return <IconVideo size={18} />;

  return (
    <button
      type="button"
      className="media-preview-button media-file-button"
      onClick={handleClick}
    >
      <IconFile size={18} />
      <span>Open</span>
    </button>
  );
}

function MediaPage() {
  const [action, setAction] = useState("index");
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState({
    isOpen: false,
    url: "",
    mediaType: "",
    mimeType: "",
  });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null });
  const pageSize = 6;

  const {
    items,
    loading,
    submitting,
    error,
    success,
    createItem,
    updateItem,
    removeItem,
  } = useMultimedia();

  const actionTitle = useMemo(() => {
    if (action === "update") return "Update media";
    return "Create media";
  }, [action]);

  function goBackToIndex() {
    setAction("index");
    setSelectedMedia(null);
  }

  const filtered = useMemo(
    () =>
      filterRows(items, search, (item) => {
        const type = item.mmd_media_type || item.mediaType || "";
        const url = item.mmd_url || item.url || "";
        const provider =
          item.mmd_storage_provider || item.storageProvider || "";
        return `${type} ${url} ${provider}`;
      }),
    [items, search],
  );

  const pagination = useMemo(
    () => paginateRows(filtered, page, pageSize),
    [filtered, page],
  );

  const columns = useMemo(
    () => [
      {
        key: "preview",
        label: "Preview",
        render: (item) => (
          <MediaPreviewCell
            item={item}
            onPreview={(data) =>
              setPreview({
                isOpen: true,
                url: data.url,
                mediaType: data.mediaType,
                mimeType: data.mimeType,
              })
            }
          />
        ),
      },
      {
        key: "type",
        label: "Type",
        render: (item) => item.mmd_media_type || item.mediaType || "-",
      },
      /*  {
        key: "url",
        label: "URL",
        render: (item) => item.mmd_url || item.url || "-",
      }, */
      {
        key: "active",
        label: "Active",
        render: (item) =>
          (item.mmd_is_active ?? item.isActive) ? "Yes" : "No",
      },
      {
        key: "actions",
        label: "Actions",
        render: (item) => (
          <div className="table-actions">
            <button
              type="button"
              className="button-link"
              onClick={() => {
                setSelectedMedia(item);
                setAction("update");
              }}
            >
              Edit
            </button>
            <button
              type="button"
              className="button-link button-danger"
              onClick={() => {
                setDeleteModal({ isOpen: true, item });
              }}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  const actionButtons = useMemo(
    () => [
      {
        key: "add",
        label: "Add",
        active: action === "add",
        onClick: () => setAction("add"),
      },
    ],
    [action],
  );

  return (
    <CmsLayout activeModule="media" title="Multimedia">
      {error ? <p className="message-error">{error}</p> : null}
      {success ? <p className="message-ok">{success}</p> : null}
      {loading ? <p>Loading media...</p> : null}

      {action === "index" ? (
        <IndexTableMap
          title="Multimedia "
          actionButtons={actionButtons}
          columns={columns}
          rows={pagination.pageRows}
          rowKey={(item) => String(item.mmd_id || item.id)}
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          page={pagination.page}
          totalPages={pagination.totalPages}
          onPrevPage={() => setPage((prev) => Math.max(1, prev - 1))}
          onNextPage={() =>
            setPage((prev) => Math.min(pagination.totalPages, prev + 1))
          }
          emptyText="No media resources found."
        />
      ) : null}

      {action !== "index" ? (
        <ActionStepMap title={actionTitle} onBack={goBackToIndex}>
          {action === "add" ? (
            <MediaAdd
              onCreate={async (payload) => {
                await createItem(payload);
                goBackToIndex();
              }}
              submitting={submitting}
            />
          ) : null}
          {action === "update" ? (
            <MediaUpdate
              media={selectedMedia}
              onUpdate={async (id, payload) => {
                await updateItem(id, payload);
                goBackToIndex();
              }}
              onCancel={goBackToIndex}
              submitting={submitting}
            />
          ) : null}
        </ActionStepMap>
      ) : null}

      <AppModal
        isOpen={preview.isOpen}
        onClose={() =>
          setPreview({ isOpen: false, url: "", mediaType: "", mimeType: "" })
        }
        title="Media Preview"
        size="xl"
      >
        {preview.mediaType.startsWith("image") ||
        preview.mimeType.startsWith("image/") ? (
          <img
            src={preview.url}
            alt="Media preview"
            className="media-modal-image"
          />
        ) : (
          <video className="media-modal-video" controls autoPlay>
            <source src={preview.url} type={preview.mimeType || undefined} />
          </video>
        )}
      </AppModal>

      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null })}
        submitting={submitting}
        title="Delete media"
        description="You are about to delete this media resource."
        preview={(() => {
          const rawUrl =
            deleteModal.item?.mmd_url || deleteModal.item?.url || "";
          const url = resolveMediaUrl(rawUrl);
          const mediaType = (
            deleteModal.item?.mmd_media_type ||
            deleteModal.item?.mediaType ||
            ""
          ).toLowerCase();
          const mimeType = (
            deleteModal.item?.mmd_mime_type ||
            deleteModal.item?.mimeType ||
            ""
          ).toLowerCase();

          if (mediaType === "image" || mimeType.startsWith("image/")) {
            return (
              <img
                src={url}
                alt="Preview to delete"
                className="media-current-thumb"
              />
            );
          }
          if (mediaType === "video" || mimeType.startsWith("video/")) {
            return (
              <video
                className="media-current-thumb"
                controls
                preload="metadata"
              >
                <source src={url} type={mimeType || undefined} />
              </video>
            );
          }
          if (mediaType === "audio" || mimeType.startsWith("audio/")) {
            return (
              <div className="media-current-file">
                <IconMusic size={18} />
                <a href={url} target="_blank" rel="noreferrer">
                  Open audio
                </a>
              </div>
            );
          }
          if (mimeType.includes("pdf")) {
            return (
              <div className="media-current-file">
                <IconFileTypePdf size={18} />
                <a href={url} target="_blank" rel="noreferrer">
                  Open PDF
                </a>
              </div>
            );
          }
          return (
            <div className="media-current-file">
              <IconFile size={18} />
              <a href={url} target="_blank" rel="noreferrer">
                Open file
              </a>
            </div>
          );
        })()}
        onConfirm={async () => {
          const id = deleteModal.item?.mmd_id || deleteModal.item?.id;
          if (!id) return;
          await removeItem(String(id));
          setDeleteModal({ isOpen: false, item: null });
        }}
      />
    </CmsLayout>
  );
}

export default MediaPage;
